'use client';

import { Breadcrumb } from '@/components/common/breadcrumb';
import { menuBreadcrumbItems } from '@/config/menu-catalog';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import { useToast } from '@/hooks/use-toast';
import { apiClient } from '@/lib/api-client';
import type { OrganizationVo, StoreResponseGasmodeEnum } from '@/lib/api/data-contracts';
import { FuelStationApiService, hasLocation, type PetroleumStation } from '@/lib/fuel-station-api';
import { HTTPError } from 'ky';
import L from 'leaflet';
import {
    FuelIcon as GasStation,
    Info,
    RefreshCw,
    Save,
    Search,
} from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { MapContainer, Marker, Popup, TileLayer, useMap } from 'react-leaflet';

function Recenter({ center, zoom }: { center: [number, number]; zoom: number }) {
    const map = useMap();
    useEffect(() => {
        map.setView(center, zoom, { animate: true });
    }, [center, zoom, map]);
    return null;
}

// Leaflet 기본 아이콘 설정 (Vite/Next 환경용)
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
    iconRetinaUrl: '/leaflet/marker-icon-2x.png',
    iconUrl: '/leaflet/marker-icon.png',
    shadowUrl: '/leaflet/marker-shadow.png',
});

interface StoreFormData {
    /** 오피넷 스테이션의 도로명주소 - 표시 전용(정본은 오피넷) */
    roadAddress: string;
    /** 오피넷 스테이션의 지번주소 - 표시 전용 */
    jibunAddress: string;
    email: string;
    /** 사업자 정보 - 사업자등록번호가 있으면 세금계산서 공급자 정보가 회사 대신 이 값으로 나간다 */
    bizNo: string;
    subBizNo: string;
    bizName: string;
    repDirector: string;
    bizType: string;
    bizItem: string;
    phone: string;
    fax: string;
    catmagam: boolean;
    posdata: boolean;
    gasmode: string;
    opinetId: string;
}

const EMPTY_BIZ_INFO = {
    bizNo: '',
    subBizNo: '',
    bizName: '',
    repDirector: '',
    bizType: '',
    bizItem: '',
    phone: '',
    fax: '',
};

/** 입력 검증(400)은 서버 사유를 그대로 보여준다 - 사업자번호 형식·필수 항목 누락을 사용자가 고칠 수 있게 */
async function saveErrorMessage(error: unknown): Promise<string> {
    const fallback = '주유소 정보 저장 중 오류가 발생했습니다.';
    if (!(error instanceof HTTPError) || error.response.status !== 400) {
        return fallback;
    }
    const body = (await error.response.json().catch(() => null)) as { errors?: { reason?: string }[] } | null;
    return body?.errors?.[0]?.reason || fallback;
}

interface StoreInfo {
    orgCd: string;
    orgName: string;
    parentOrgCd?: string;
    parentOrgName?: string;
}

export function StationManagement() {
    const { toast } = useToast();
    const [stores, setStores] = useState<StoreInfo[]>([]);
    const [selectedStore, setSelectedStore] = useState<StoreInfo | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const [storeFormData, setStoreFormData] = useState<StoreFormData>({
        roadAddress: '',
        jibunAddress: '',
        email: '',
        ...EMPTY_BIZ_INFO,
        catmagam: false,
        posdata: false,
        gasmode: '',
        opinetId: '',
    });

    // 주유소 검색 상태
    const [stationSearchQuery, setStationSearchQuery] = useState('');
    const [stationResults, setStationResults] = useState<PetroleumStation[]>([]);
    const [isSearchingStations, setIsSearchingStations] = useState(false);
    const [mapCenter, setMapCenter] = useState<[number, number]>([37.5665, 126.978]);
    const [miniMapZoom, setMiniMapZoom] = useState(13);

    // 주유소 찾기 모달 상태
    const [stationSearchModalOpen, setStationSearchModalOpen] = useState(false);
    const [tempSelectedStation, setTempSelectedStation] = useState<PetroleumStation | null>(null);

    // 주유소 목록 조회
    const fetchStores = useCallback(async () => {
        setIsLoading(true);
        try {
            const response = await apiClient.get('admin/organization');
            const data = (await response.json()) as OrganizationVo[];

            // STORE 타입만 필터링하고 정렬
            const storeList = data
                .filter(item => item.type === 'STORE')
                .map(store => ({
                    orgCd: store.orgCd || '',
                    orgName: store.orgName || '',
                    parentOrgCd: store.parentOrgCd,
                    parentOrgName: findParentName(data, store.parentOrgCd),
                }))
                .sort((a, b) => a.orgName.localeCompare(b.orgName));

            setStores(storeList);
        } catch {
            toast({
                title: '주유소 목록 조회 실패',
                description: '주유소 목록을 불러오는 중 오류가 발생했습니다',
                variant: 'destructive',
            });
        } finally {
            setIsLoading(false);
        }
    }, [toast]);

    // 부모 조직명 찾기
    const findParentName = (data: OrganizationVo[], parentOrgCd?: string): string => {
        if (!parentOrgCd) return '미할당';
        const parent = data.find(item => item.orgCd === parentOrgCd);
        return parent?.orgName || '알 수 없음';
    };

    // 주유소 선택 처리
    const handleStoreSelect = (store: StoreInfo) => {
        setSelectedStore(store);
        loadStoreData(store.orgCd);
        // 주유소 선택 시 검색 상태 초기화
        setStationResults([]);
        setStationSearchQuery('');
    };

    // 주유소 데이터 로드
    const loadStoreData = async (storeId: string) => {
        try {
            const response = await apiClient.get(`admin/organization/store/${storeId}`);
            const data = (await response.json()) as any;
            setStoreFormData({
                // 주소는 오피넷이 정본이라 저장하지 않는다 - opinetId 로 조회해 아래에서 채운다
                roadAddress: '',
                jibunAddress: '',
                email: data.email || '',
                bizNo: data.bizNo || '',
                subBizNo: data.subBizNo || '',
                bizName: data.bizName || '',
                repDirector: data.repDirector || '',
                bizType: data.bizType || '',
                bizItem: data.bizItem || '',
                phone: data.phone || '',
                fax: data.fax || '',
                catmagam: data.catmagam || false,
                posdata: data.posdata || false,
                gasmode: data.gasmode || '',
                opinetId: data.opinetId || '',
            });

            // opinetId가 있으면 주유소 상세 정보 조회하여 주소와 지도 위치 세팅
            if (data.opinetId) {
                await loadStationDetail(data.opinetId);
            }
        } catch {
            toast({
                title: '주유소 정보 로드 실패',
                description: '주유소 정보를 불러오는 중 오류가 발생했습니다',
                variant: 'destructive',
            });
        }
    };

    // 주유소 상세 정보 조회 및 주소/지도 위치 세팅
    const loadStationDetail = async (opinetId: string) => {
        try {
            const stationDetail = await FuelStationApiService.getStationDetail(opinetId);
            if (stationDetail) {
                setStoreFormData(prev => ({
                    ...prev,
                    roadAddress: stationDetail.roadAddress ?? '',
                    jibunAddress: stationDetail.jibunAddress ?? '',
                }));

                // 지도 중심과 줌 설정 (좌표가 없으면 이동하지 않음)
                if (hasLocation(stationDetail)) {
                    setMapCenter([stationDetail.latitude, stationDetail.longitude]);
                    setMiniMapZoom(15);
                }
            }
        } catch (error) {
            console.error('Failed to load station detail:', error);
        }
    };

    // 주유소 텍스트 검색
    const handleSearchStations = useCallback(async () => {
        const q = stationSearchQuery.trim();
        if (q.length < 2) {
            toast({
                title: '검색어가 짧습니다',
                description: '두 글자 이상 입력해주세요.',
                variant: 'destructive',
            });
            return;
        }
        setIsSearchingStations(true);
        try {
            const results = await FuelStationApiService.searchByText(q);
            setStationResults(results.slice(0, 20));
            if (results.length === 0) {
                toast({ title: '검색 결과 없음', description: '일치하는 주유소가 없습니다.' });
            }
        } catch {
            toast({
                title: '검색 실패',
                description: '주유소 검색 중 오류가 발생했습니다.',
                variant: 'destructive',
            });
        } finally {
            setIsSearchingStations(false);
        }
    }, [stationSearchQuery, toast]);

    // 모달에서 주유소 임시 선택 (지도 이동만)
    const handleTempSelectStation = useCallback((station: PetroleumStation) => {
        setTempSelectedStation(station);
        if (!hasLocation(station)) return;
        setMapCenter([station.latitude, station.longitude]);
        setMiniMapZoom(15);
    }, []);

    // 주유소 선택 적용 (실제 데이터 저장)
    const handleApplySelectedStation = useCallback(() => {
        if (tempSelectedStation) {
            setStoreFormData(prev => ({
                ...prev,
                opinetId: tempSelectedStation.id,
                roadAddress: tempSelectedStation.roadAddress ?? '',
                jibunAddress: tempSelectedStation.jibunAddress ?? ''
            }));
            setStationSearchModalOpen(false);
            setTempSelectedStation(null);
        }
    }, [tempSelectedStation]);

    // 주유소 찾기 모달 열기
    const handleOpenStationSearchModal = useCallback(() => {
        setStationSearchModalOpen(true);
        setStationSearchQuery('');
        setStationResults([]);
        setTempSelectedStation(null);
    }, []);

    // 모달 취소 (닫기)
    const handleCancelStationSearch = useCallback(() => {
        setStationSearchModalOpen(false);
        setTempSelectedStation(null);
    }, []);

    // UI 값을 API enum으로 매핑
    const mapUiGasmodeToApi = (value: string): StoreResponseGasmodeEnum | undefined => {
        if (!value) return undefined;
        if (value === 'ELECTRIC') return 'ELECTRIC';
        if (value === 'GAS') return 'GASOLINE';
        return value as StoreResponseGasmodeEnum;
    };

    // 주유소 정보 저장 API 연동
    const handleSaveStore = useCallback(async () => {
        if (!selectedStore) {
            toast({ title: '저장 불가', description: '주유소를 선택해주세요.', variant: 'destructive' });
            return;
        }

        const payload = {
            email: storeFormData.email || undefined,
            // PUT 은 전체 치환이라 빈 값도 보내야 지워진다(서버가 빈 문자열을 NULL 로 저장)
            bizNo: storeFormData.bizNo,
            subBizNo: storeFormData.subBizNo,
            bizName: storeFormData.bizName,
            repDirector: storeFormData.repDirector,
            bizType: storeFormData.bizType,
            bizItem: storeFormData.bizItem,
            phone: storeFormData.phone,
            fax: storeFormData.fax,
            catmagam: storeFormData.catmagam,
            posdata: storeFormData.posdata,
            gasmode: mapUiGasmodeToApi(storeFormData.gasmode || ''),
            opinetId: storeFormData.opinetId || undefined,
        };

        setIsSubmitting(true);
        try {
            await apiClient.put(`admin/organization/store/${selectedStore.orgCd}`, { json: payload });
            toast({ title: '저장 완료', description: '주유소 정보가 저장되었습니다.' });
            // 저장 후 최신 정보 재조회
            await loadStoreData(selectedStore.orgCd);
        } catch (error) {
            toast({ title: '저장 실패', description: await saveErrorMessage(error), variant: 'destructive' });
        } finally {
            setIsSubmitting(false);
        }
    }, [selectedStore, storeFormData, toast]);

    // 검색 결과에 따라 지도 중심 업데이트
    useEffect(() => {
        if (stationResults.length > 0) {
            const first = stationResults[0];
            if (first.latitude && first.longitude) {
                setMapCenter([first.latitude, first.longitude]);
            }
        }
    }, [stationResults]);

    // 초기 데이터 로드
    useEffect(() => {
        fetchStores();
    }, [fetchStores]);

    return (
        <div className='p-3 space-y-3'>
            {/* 브레드크럼 */}
            <Breadcrumb items={menuBreadcrumbItems('station-management')} />

            <div className='bg-blue-50 border border-blue-200 rounded-lg p-3'>
                <div className='flex items-center text-blue-600'>
                    <Info className='h-4 w-4 mr-2' />
                    <span className='text-sm'>
                        주유소를 선택하여 상세 정보를 수정할 수 있습니다.
                        <strong>찾기</strong> 버튼을 사용하여 Opinet API에서 주유소 정보를 검색할 수 있습니다.
                    </span>
                </div>
            </div>

            <div className='grid grid-cols-1 lg:grid-cols-3 gap-6'>
                {/* 주유소 목록 */}
                <Card>
                    <CardHeader>
                        <div className='flex items-center justify-between'>
                            <CardTitle className='flex items-center gap-2'>
                                <GasStation className='h-4 w-4' />
                                주유소 목록
                            </CardTitle>
                            <Button
                                size='icon'
                                onClick={fetchStores}
                                disabled={isLoading}
                            >
                                <RefreshCw
                                    className={`h-4 w-4 ${isLoading ? 'animate-spin' : ''}`}
                                />
                            </Button>
                        </div>
                    </CardHeader>
                    <CardContent>
                        <div className='h-96 border rounded-lg overflow-auto'>
                            {stores.length > 0 ? (
                                <div className='divide-y'>
                                    {stores.map(store => (
                                        <button
                                            key={store.orgCd}
                                            className={`w-full text-left p-3 hover:bg-gray-50 transition-colors ${selectedStore?.orgCd === store.orgCd
                                                ? 'bg-blue-50 border-r-2 border-blue-500'
                                                : ''
                                                }`}
                                            onClick={() => handleStoreSelect(store)}
                                        >
                                            <div className='font-medium text-sm'>{store.orgName}</div>
                                            <div className='text-xs text-gray-500 mt-1'>
                                                {store.parentOrgName}
                                            </div>
                                            <div className='text-xs text-gray-400'>
                                                {store.orgCd}
                                            </div>
                                        </button>
                                    ))}
                                </div>
                            ) : (
                                <div className='flex items-center justify-center h-full text-gray-500'>
                                    {isLoading ? '로딩 중...' : '주유소 데이터가 없습니다'}
                                </div>
                            )}
                        </div>
                    </CardContent>
                </Card>

                {/* 주유소 정보 폼 */}
                <Card className='lg:col-span-2'>
                    <CardHeader>
                        <CardTitle className='flex items-center gap-2'>
                            <GasStation className='h-4 w-4' />
                            주유소 정보
                            {selectedStore && (
                                <span className='text-sm font-normal text-gray-500'>
                                    - {selectedStore.orgName}
                                </span>
                            )}
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        {selectedStore ? (
                            <div className='space-y-4'>
                                <div>
                                    <Label>도로명주소</Label>
                                    <div className='flex gap-2'>
                                        <Input
                                            readOnly
                                            value={storeFormData.roadAddress}
                                            placeholder='주유소를 찾으면 자동으로 채워집니다'
                                            className='flex-1'
                                        />
                                        <Button
                                            variant="outline"
                                            onClick={handleOpenStationSearchModal}
                                            type="button"
                                        >
                                            찾기
                                        </Button>
                                    </div>
                                    <p className='text-xs text-muted-foreground mt-1'>
                                        주소는 오피넷 주유소 정보에서 가져옵니다. 세금계산서의 공급자 주소로 도로명주소가 쓰입니다.
                                    </p>
                                </div>

                                <div>
                                    <Label>지번주소</Label>
                                    <Input readOnly value={storeFormData.jibunAddress} placeholder='-' />
                                </div>

                                <div>
                                    <Label>대표 이메일</Label>
                                    <Input
                                        type='email'
                                        value={storeFormData.email}
                                        onChange={e => setStoreFormData(prev => ({ ...prev, email: e.target.value }))}
                                        placeholder='세금계산서 공급자 이메일'
                                    />
                                    <p className='text-xs text-muted-foreground mt-1'>
                                        세금계산서의 공급자 이메일로 쓰입니다. 비워 두면 회사 이메일이 대신 나갑니다.
                                    </p>
                                </div>

                                <div className='space-y-3 rounded-md border p-3'>
                                    <div>
                                        <div className='text-sm font-medium'>사업자 정보</div>
                                        <p className='text-xs text-muted-foreground mt-1'>
                                            사업자등록번호를 입력하면 세금계산서·거래명세서의 공급자 등록번호·상호·대표자·업태·종목이
                                            회사 대신 이 값으로 나갑니다. 이때 상호·대표자·업태·종목은 필수입니다.
                                            비워 두면 회사 정보가 쓰입니다.
                                        </p>
                                    </div>
                                    <div className='grid grid-cols-1 lg:grid-cols-2 gap-4'>
                                        <div>
                                            <Label>사업자등록번호</Label>
                                            <Input
                                                value={storeFormData.bizNo}
                                                onChange={e => setStoreFormData(prev => ({ ...prev, bizNo: e.target.value }))}
                                                placeholder='000-00-00000' inputMode='numeric'
                                            />
                                        </div>
                                        <div>
                                            <Label>종사업장번호</Label>
                                            <Input
                                                value={storeFormData.subBizNo}
                                                onChange={e => setStoreFormData(prev => ({ ...prev, subBizNo: e.target.value }))}
                                                placeholder='0000' inputMode='numeric' maxLength={4}
                                            />
                                        </div>
                                        <div>
                                            <Label>상호</Label>
                                            <Input
                                                value={storeFormData.bizName}
                                                onChange={e => setStoreFormData(prev => ({ ...prev, bizName: e.target.value }))}
                                                placeholder='법정 상호'
                                            />
                                        </div>
                                        <div>
                                            <Label>대표자</Label>
                                            <Input
                                                value={storeFormData.repDirector}
                                                onChange={e => setStoreFormData(prev => ({ ...prev, repDirector: e.target.value }))}
                                                placeholder='대표자 성명'
                                            />
                                        </div>
                                        <div>
                                            <Label>업태</Label>
                                            <Input
                                                value={storeFormData.bizType}
                                                onChange={e => setStoreFormData(prev => ({ ...prev, bizType: e.target.value }))}
                                                placeholder='예: 도매 및 소매업'
                                            />
                                        </div>
                                        <div>
                                            <Label>종목</Label>
                                            <Input
                                                value={storeFormData.bizItem}
                                                onChange={e => setStoreFormData(prev => ({ ...prev, bizItem: e.target.value }))}
                                                placeholder='예: 주유소 운영업'
                                            />
                                        </div>
                                        <div>
                                            <Label>전화번호</Label>
                                            <Input
                                                value={storeFormData.phone}
                                                onChange={e => setStoreFormData(prev => ({ ...prev, phone: e.target.value }))}
                                                placeholder='031-000-0000' type='tel'
                                            />
                                        </div>
                                        <div>
                                            <Label>팩스번호</Label>
                                            <Input
                                                value={storeFormData.fax}
                                                onChange={e => setStoreFormData(prev => ({ ...prev, fax: e.target.value }))}
                                                placeholder='031-000-0000' type='tel'
                                            />
                                        </div>
                                    </div>
                                </div>

                                <div className='grid grid-cols-1 lg:grid-cols-2 gap-4'>
                                    <div>
                                        <Label>캣마감</Label>
                                        <RadioGroup
                                            value={storeFormData.catmagam.toString()}
                                            onValueChange={value =>
                                                setStoreFormData({
                                                    ...storeFormData,
                                                    catmagam: value === 'true',
                                                })
                                            }
                                        >
                                            <div className='flex items-center space-x-2'>
                                                <RadioGroupItem value='true' id='catmagam-yes' />
                                                <Label htmlFor='catmagam-yes'>Yes</Label>
                                            </div>
                                            <div className='flex items-center space-x-2'>
                                                <RadioGroupItem value='false' id='catmagam-no' />
                                                <Label htmlFor='catmagam-no'>No</Label>
                                            </div>
                                        </RadioGroup>
                                    </div>

                                    <div>
                                        <Label>포스데이터</Label>
                                        <RadioGroup
                                            value={storeFormData.posdata.toString()}
                                            onValueChange={value =>
                                                setStoreFormData({
                                                    ...storeFormData,
                                                    posdata: value === 'true',
                                                })
                                            }
                                        >
                                            <div className='flex items-center space-x-2'>
                                                <RadioGroupItem value='true' id='posdata-yes' />
                                                <Label htmlFor='posdata-yes'>Yes</Label>
                                            </div>
                                            <div className='flex items-center space-x-2'>
                                                <RadioGroupItem value='false' id='posdata-no' />
                                                <Label htmlFor='posdata-no'>No</Label>
                                            </div>
                                        </RadioGroup>
                                    </div>
                                </div>

                                <div>
                                    <Label>Station</Label>
                                    <Select
                                        value={storeFormData.gasmode}
                                        onValueChange={value =>
                                            setStoreFormData({ ...storeFormData, gasmode: value })
                                        }
                                    >
                                        <SelectTrigger className='w-full lg:w-1/3'>
                                            <SelectValue placeholder='Station 선택' />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value='GAS'>GAS</SelectItem>
                                            <SelectItem value='ELECTRIC'>ELECTRIC</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>

                                <div className='flex justify-end'>
                                    <Button onClick={handleSaveStore} disabled={isSubmitting}>
                                        <Save className='h-4 w-4 mr-2' />
                                        저장
                                    </Button>
                                </div>
                            </div>
                        ) : (
                            <div className='flex items-center justify-center h-64 text-gray-500'>
                                주유소를 선택해주세요
                            </div>
                        )}
                    </CardContent>
                </Card>
            </div>

            {/* 주유소 찾기 모달 */}
            <Dialog open={stationSearchModalOpen} onOpenChange={handleCancelStationSearch}>
                <DialogContent className='max-w-4xl max-h-[80vh] overflow-hidden'>
                    <DialogHeader>
                        <DialogTitle>주유소 찾기</DialogTitle>
                    </DialogHeader>
                    <div className='flex flex-col h-[600px]'>
                        {/* 검색 입력 */}
                        <div className='flex gap-2 mb-4'>
                            <Input
                                value={stationSearchQuery}
                                onChange={e => setStationSearchQuery(e.target.value)}
                                onKeyDown={e => {
                                    if (e.key === 'Enter' && !isSearchingStations) {
                                        e.preventDefault();
                                        handleSearchStations();
                                    }
                                }}
                                placeholder='주유소명 또는 주소로 검색'
                                className='flex-1'
                            />
                            <Button onClick={handleSearchStations} disabled={isSearchingStations}>
                                <Search className='h-4 w-4 mr-1' />
                                {isSearchingStations ? '검색 중...' : '검색'}
                            </Button>
                        </div>

                        <div className='flex gap-4 flex-1 min-h-0'>
                            {/* 검색 결과 목록 */}
                            <div className='w-1/3 border rounded-md'>
                                <div className='p-3 border-b bg-gray-50'>
                                    <h3 className='font-medium text-sm'>
                                        검색 결과 ({stationResults.length}개)
                                    </h3>
                                </div>
                                <div className='overflow-auto max-h-[90%]'>
                                    {stationResults.length > 0 ? (
                                        <div className='divide-y'>
                                            {stationResults.map(station => (
                                                <button
                                                    key={station.id}
                                                    className={`w-full text-left px-3 py-3 hover:bg-gray-50 transition-colors ${tempSelectedStation?.id === station.id
                                                        ? 'bg-blue-50 border-r-2 border-blue-500'
                                                        : ''
                                                        }`}
                                                    onClick={() => handleTempSelectStation(station)}
                                                >
                                                    <div className='text-sm font-medium truncate'>{station.name}</div>
                                                    <div className='text-xs text-muted-foreground truncate mt-1'>
                                                        {station.address}
                                                    </div>
                                                    {!hasLocation(station) && (
                                                        <div className='text-xs text-amber-600 mt-1'>
                                                            좌표 정보가 없어 지도에 표시되지 않습니다.
                                                        </div>
                                                    )}
                                                </button>
                                            ))}
                                        </div>
                                    ) : (
                                        <div className='p-4 text-center text-gray-500 text-sm'>
                                            {isSearchingStations ? '검색 중...' : '검색어를 입력하고 검색해주세요'}
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* 지도 */}
                            <div className='flex-1 border rounded-md overflow-hidden'>
                                <MapContainer
                                    center={mapCenter}
                                    zoom={miniMapZoom}
                                    scrollWheelZoom={true}
                                    className='h-full w-full'
                                >
                                    <Recenter center={mapCenter} zoom={miniMapZoom} />
                                    <TileLayer
                                        attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                                        url='https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png'
                                    />
                                    {stationResults.filter(hasLocation).map(station => (
                                        <Marker
                                            key={station.id}
                                            position={[station.latitude, station.longitude]}
                                            eventHandlers={{
                                                click: () => handleTempSelectStation(station),
                                            }}
                                        >
                                            <Popup>
                                                <div className='min-w-[180px]'>
                                                    <div className='font-medium text-sm'>{station.name}</div>
                                                    <div className='text-xs text-gray-600 mt-1'>{station.address}</div>
                                                </div>
                                            </Popup>
                                        </Marker>
                                    ))}
                                    {tempSelectedStation && hasLocation(tempSelectedStation) && (
                                        <Marker
                                            key={`temp-selected-${tempSelectedStation.id}`}
                                            position={[tempSelectedStation.latitude, tempSelectedStation.longitude]}
                                        >
                                            <Popup>
                                                <div className='min-w-[180px]'>
                                                    <div className='font-medium text-sm text-blue-600'>{tempSelectedStation.name}</div>
                                                    <div className='text-xs text-gray-600 mt-1'>{tempSelectedStation.address}</div>
                                                    <div className='text-xs text-blue-600 mt-1'>미리보기 선택</div>
                                                </div>
                                            </Popup>
                                        </Marker>
                                    )}
                                </MapContainer>
                            </div>
                        </div>

                        {/* 하단 버튼 */}
                        <div className='flex justify-between items-center mt-4 pt-4 border-t'>
                            <div className='text-sm text-gray-600'>
                                {tempSelectedStation
                                    ? `선택됨: ${tempSelectedStation.name}`
                                    : '주유소를 선택해주세요'}
                            </div>
                            <div className='flex gap-2'>
                                <Button variant="outline" onClick={handleCancelStationSearch}>
                                    취소
                                </Button>
                                <Button
                                    onClick={handleApplySelectedStation}
                                    disabled={!tempSelectedStation}
                                >
                                    적용
                                </Button>
                            </div>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>
        </div>
    );
}
